/**
 * Content and downstream stage requests on an analysis run: shallow and
 * deep content enrichment and review, audience signals, comparisons,
 * finding candidates and review, opportunities, creative directions, and
 * the bounded corpus collection and import.
 */

import { request } from './request.js';

export function enrichAnalysisContent(
  runId: string,
  idempotencyKey: string,
  body: Readonly<Record<string, unknown>> = {},
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/content/shallow-enrich`,
    { idempotencyKey, body },
  );
}

export function importDeepAnalysisContent(
  runId: string,
  idempotencyKey: string,
  body: Readonly<Record<string, unknown>>,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/content/deep/import`,
    { idempotencyKey, body },
  );
}

export function reviewDeepAnalysisContent(
  runId: string,
  idempotencyKey: string,
  body: Readonly<Record<string, unknown>>,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/content/deep/review`,
    { idempotencyKey, body },
  );
}

export function continueAnalysisWithShallowEvidence(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/content/deep/continue-with-shallow`,
    { idempotencyKey },
  );
}

export function extractAnalysisAudienceSignals(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/audience-signals/extract`,
    { idempotencyKey },
  );
}

export function reviewAnalysisAudienceSignals(
  runId: string,
  idempotencyKey: string,
  body: Readonly<Record<string, unknown>>,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/audience-signals/review`,
    { idempotencyKey, body },
  );
}

export function runAnalysisComparisons(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/comparative-analysis/run`,
    { idempotencyKey },
  );
}

export function generateAnalysisFindingCandidates(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/finding-candidates/generate`,
    { idempotencyKey },
  );
}

export function reviewAnalysisFindings(
  runId: string,
  idempotencyKey: string,
  body: Readonly<Record<string, unknown>>,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/findings/review`,
    { idempotencyKey, body },
  );
}

export function generateAnalysisOpportunityCandidates(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/opportunity-candidates/generate`,
    { idempotencyKey },
  );
}

export function reviewAnalysisOpportunities(
  runId: string,
  idempotencyKey: string,
  body: Readonly<Record<string, unknown>>,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/opportunities/review`,
    { idempotencyKey, body },
  );
}

export function generateAnalysisCreativeDirections(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/creative-directions/generate`,
    { idempotencyKey },
  );
}

export function reviewAnalysisCreativeDirections(
  runId: string,
  idempotencyKey: string,
  body: Readonly<Record<string, unknown>>,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/creative-directions/review`,
    { idempotencyKey, body },
  );
}

export function collectAnalysisContent(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/content/collect`,
    {
      idempotencyKey,
    },
  );
}

export function importAnalysisContent(
  runId: string,
  idempotencyKey: string,
  body: Readonly<Record<string, unknown>>,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/content/import`,
    {
      idempotencyKey,
      body,
    },
  );
}
